<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';

const WORD_LIMIT = 120;

const props = defineProps({
  bio: {
    type: String,
    default: '',
  },
  // The bio is server-sanitized HTML rather than plain text. Rendered as-is and
  // collapsed by height, because word slicing would cut markup mid-tag.
  html: {
    type: Boolean,
    default: false,
  },
});

const expanded = ref(false);
const textRef = ref(null);
const htmlOverflows = ref(false);

const allWords = computed(() => (props.bio || '').split(/\s+/).filter(Boolean));
const isTruncatable = computed(() =>
  props.html ? htmlOverflows.value : allWords.value.length > WORD_LIMIT
);

const formatHtml = text => {
  if (!text) return '';
  return text
    .trim()
    .split(/\n\s*\n/)
    .map(para => `<p>${para.trim().replace(/\n/g, '<br>')}</p>`)
    .join('');
};

const truncatedText = computed(() => `${allWords.value.slice(0, WORD_LIMIT).join(' ')}…`);

const displayedHtml = computed(() => {
  if (props.html) return props.bio;

  return expanded.value || !isTruncatable.value
    ? formatHtml(props.bio)
    : formatHtml(truncatedText.value);
});

onMounted(async () => {
  if (!props.html) return;

  await nextTick();
  const el = textRef.value;
  if (el) htmlOverflows.value = el.scrollHeight > el.clientHeight + 1;
});

const toggleExpanded = () => {
  expanded.value = !expanded.value;
};
</script>

<template>
  <div class="person-profile-modal__bio">
    <div class="person-profile-modal__bio-wrapper">
      <div
        ref="textRef"
        class="person-profile-modal__bio-text"
        :class="{ 'is-clamped': html && !expanded }"
        v-html="displayedHtml"
      ></div>

      <button
        v-if="isTruncatable && !expanded"
        type="button"
        class="person-profile-modal__bio-toggle"
        @click="toggleExpanded"
      >
        Read all
      </button>

      <button
        v-if="isTruncatable && expanded"
        type="button"
        class="person-profile-modal__bio-toggle is-collapse"
        @click="toggleExpanded"
      >
        <span class="person-profile-modal__bio-toggle-icon" aria-hidden="true">
          <svg viewBox="0 0 1001.79 901.97" xmlns="http://www.w3.org/2000/svg">
            <line
              fill="none"
              stroke-linecap="round"
              vector-effect="non-scaling-stroke"
              x1="0.7"
              x2="999.79"
              y1="450.8"
              y2="450.8"
            ></line>
            <polyline
              fill="none"
              points="461.58 0.99 1000.7 450.8 461.58 900.99"
              stroke-linecap="round"
              vector-effect="non-scaling-stroke"
            ></polyline>
          </svg>
        </span>
        Read less
      </button>
    </div>
  </div>
</template>

<style scoped></style>
